import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-ocean-50 to-sand-50 p-8">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-ocean-600 hover:text-ocean-700 mb-6"
        >
          <ArrowLeft size={20} />
          Back
        </button>

        <h1 className="text-3xl font-bold text-ocean-900 mb-6">Privacy Policy</h1>
        <p className="text-gray-600 mb-6">Last updated: January 12, 2026</p>

        <div className="space-y-6 text-gray-700">
          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">1. Introduction</h2>
            <p>
              Welcome to BabyBookin ("we," "our," or "us"). We respect your privacy and are committed to protecting your personal data. 
              This privacy policy explains how we collect, use, and safeguard your information when you use our baby book application.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">2. Information We Collect</h2>
            <p className="mb-2">We collect the following types of information:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Account Information:</strong> Email address, name, and authentication credentials</li>
              <li><strong>Baby Book Data:</strong> Baby's name, birth information, family details, and custom content you create</li>
              <li><strong>Photos:</strong> Images you upload to your baby book</li>
              <li><strong>Journal Entries:</strong> Text entries, milestones, and memories you record</li>
              <li><strong>Usage Data:</strong> Information about how you interact with the app</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">3. How We Use Your Information</h2>
            <p className="mb-2">We use your information to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide and maintain the BabyBookin service</li>
              <li>Create and manage your baby book</li>
              <li>Enable collaboration with family members you invite</li>
              <li>Improve and personalize your experience</li>
              <li>Send important service notifications</li>
              <li>Respond to your support requests</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">4. Data Storage and Security</h2>
            <p className="mb-2">
              Your data is stored securely using Google Firebase services with the following protections:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Industry-standard encryption for data in transit and at rest</li>
              <li>Secure authentication via Firebase Authentication</li>
              <li>Role-based access controls to protect your baby books</li>
              <li>Regular security updates and monitoring</li>
              <li>Data backup and redundancy through Firebase infrastructure</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">5. Data Sharing and Disclosure</h2>
            <p className="mb-2">We do NOT sell your personal information. We only share your data:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>With family members you explicitly invite to your baby book</li>
              <li>With service providers (Google Firebase) who help us operate the app</li>
              <li>When required by law or to protect our legal rights</li>
              <li>With your explicit consent</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">6. Your Rights and Choices</h2>
            <p className="mb-2">You have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access, update, or delete your personal information</li>
              <li>Export your baby book data</li>
              <li>Control who can view your baby books</li>
              <li>Delete your account and all associated data</li>
              <li>Opt out of non-essential communications</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">7. Children's Privacy</h2>
            <p>
              BabyBookin is designed for parents and family members to document their children's lives. 
              We do not knowingly collect personal information directly from children under 13. 
              Parents are responsible for the content they create about their children.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">8. Data Retention</h2>
            <p>
              We retain your information for as long as your account is active or as needed to provide services. 
              If you delete your account, we will delete your personal data within 30 days, 
              except where retention is required by law.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">9. International Data Transfers</h2>
            <p>
              Your data may be transferred to and stored on servers located outside your country. 
              We use Google Firebase's global infrastructure, which complies with applicable data protection laws.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">10. Changes to This Policy</h2>
            <p>
              We may update this privacy policy from time to time. We will notify you of significant changes 
              by email or through the app. Your continued use after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">11. Contact Us</h2>
            <p>
              If you have questions about this privacy policy or your data, please contact us at:
            </p>
            <p className="mt-2">
              <strong>Email:</strong> support@babybookin.com<br />
              <strong>Website:</strong> www.babybookin.com
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}